import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useTheme } from '@/ui/theme'
import { Tooltip } from '@heroui/react'
import {
  RefundPaymentMethod,
  setRefundPaymentMethod,
  setKeepYourItem
} from '@/redux/return/return-slice'
import { RefundTimings } from '@/redux/return/return-slice'
import BankTransfer from './bank-transfer.svg'
import OPM from './opm.svg'
import { useTranslate } from '@/hooks'
import { toRGB } from '@/utils'
import {
  KeepYourItem as RestKeepYourItem,
  RefundMethod as RestRefundMethod,
  RDVSurplus as rdvSurplus,
  ShippingCosts as RestShippingCosts,
  ServiceFees as RestServiceFees
} from '@itsrever/returns-api-types'
import { useTranslation } from 'react-i18next'
import { ShippingCosts } from './shipping-costs'
import RDVSurplusBadge from './rdv-surplus-badge'
import DeliveryIcon from '@/app/refund-methods/v1/refund-card/delivery-icon'
import { formatStringPrice } from '@/utils'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import clsx from 'clsx'
interface RefundCardProps {
  method: RestRefundMethod
  keepYourItem?: RestKeepYourItem
  totalRefundPrice: number
  keepYourItemOffered?: boolean
  selected?: boolean
  rdvSurplus?: rdvSurplus
  shippingCosts?: RestShippingCosts
  giftCardOffered?: boolean
  serviceFees?: RestServiceFees
}

export const RefundCard: React.FC<RefundCardProps> = ({
  method,
  totalRefundPrice,
  keepYourItemOffered,
  selected = false,
  rdvSurplus,
  shippingCosts,
  giftCardOffered = false,
  serviceFees
}) => {
  const { translate } = useTranslate()
  const { i18n, t } = useTranslation()
  const lang = i18n.language

  const dispatch = useAppDispatch()
  const theme = useTheme()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const isReverLite = settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

  const instantMethod =
    method.method === RefundPaymentMethod.GiftCard ||
    method.method === RefundPaymentMethod.PromoCode

  const tagBackgroundColor = instantMethod
    ? '#1b75eb'
    : selected
      ? theme.colors.primary.main
      : theme.colors.grey[0]
  const tagColor = theme.colors.common.white

  const iconSource =
    method.method === RefundPaymentMethod.BankTransfer ? BankTransfer : OPM

  const isRDVSurplus =
    (rdvSurplus?.surplus_amount ?? 0) > 0 ||
    (rdvSurplus?.surplus_percentage ?? 0) > 0

  const currency =
    useAppSelector(
      (store) => store.returnsApi.order.response?.order?.money_format
    )?.currency ?? 'unknown'

  const getRDVAmount = (amount: number) => {
    const amountDivided = (amount ?? 0) / 100
    return formatStringPrice(amountDivided.toString(), currency, lang)
  }

  const totalAmount = totalRefundPrice
  const totalAmountMinusShippingCosts = () => {
    if (instantMethod || !giftCardOffered) {
      return totalAmount - parseFloat(shippingCosts?.min?.amount || '0')
    } else {
      return totalAmount - parseFloat(shippingCosts?.max?.amount || '0')
    }
  }
  const returnTotalAmount = formatStringPrice(
    totalAmountMinusShippingCosts().toString(),
    currency,
    lang
  )
  const freeShipping = parseFloat(shippingCosts?.max?.amount || '0') === 0
  const freeAvailable = parseFloat(shippingCosts?.min?.amount || '0') === 0
  const maxShippingCots = formatStringPrice(
    shippingCosts?.max?.amount || '0',
    currency,
    lang
  )
  const totalRDVSurplus = () => {
    if (rdvSurplus?.surplus_amount) {
      const amount = totalAmount + rdvSurplus?.surplus_amount / 100
      return formatStringPrice(amount.toString(), currency, lang)
    } else if (rdvSurplus?.surplus_percentage) {
      const amount =
        totalAmount + totalAmount * (rdvSurplus?.surplus_percentage / 100)
      return formatStringPrice(amount.toString(), currency, lang)
    } else {
      return formatStringPrice(totalAmount.toString(), currency, lang)
    }
  }

  const serviceFeeAmount = () => {
    if (serviceFees?.service_fees_amount) {
      return formatStringPrice(
        (serviceFees.service_fees_amount / 100).toString(),
        currency,
        lang
      )
    } else if (serviceFees?.service_fees_percentage) {
      const amount = totalAmount * (serviceFees.service_fees_percentage / 100)
      return formatStringPrice(amount.toString(), currency, lang)
    } else {
      return ''
    }
  }

  const logoUrl = settings.branding?.logo_url

  const isHoff = settings.ecommerce_group == 'hoff'
  const isMisako = settings.ecommerce_group == 'misako'
  const shouldShowServiceFees =
    !serviceFees?.apply_only_if_payment_at_checkout &&
    (serviceFees?.service_fees_amount || serviceFees?.service_fees_percentage)

  if (isHoff) {
    return (
      <div
        id={method.method?.toString()}
        className="relative flex content-center items-center border-b-1 border-solid border-black"
        onClick={() => {
          dispatch(setRefundPaymentMethod(method ?? null))
          dispatch(setKeepYourItem(false))
        }}
      >
        <div
          className={clsx(
            'w-full cursor-pointer border-t-1 border-solid border-black px-4 py-3 hover:bg-neutral-100',
            selected && 'bg-neutral-100'
          )}
        >
          <div className="flex justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="flex items-center gap-2">
                {instantMethod ? (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={
                      selected
                        ? 'z-[5] size-[15px] overflow-visible [&>#TOP]:animate-spin-gift'
                        : 'z-[5] size-[15px] overflow-visible'
                    }
                  >
                    <path
                      id="TOP"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.5714 6.881V4.85719C14.5714 4.41011 14.209 4.04767 13.7619 4.04767H9.27714L11.819 2.10481C11.9908 1.97599 12.1043 1.78421 12.1347 1.57167C12.1651 1.35913 12.1097 1.14323 11.981 0.971475L11.4952 0.323856C11.227 -0.0338126 10.7196 -0.1063 10.3619 0.161951L7.28571 2.53386L4.20952 0.161951C3.85185 -0.1063 3.34444 -0.0338126 3.07619 0.323856L2.59048 0.971475C2.32222 1.32915 2.39471 1.83656 2.75238 2.10481L5.29429 4.04767H0.809524C0.36244 4.04767 0 4.41011 0 4.85719V6.881H14.5714Z"
                      fill="#5E5E63"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.817619 9.30957H6.07952L6.07143 14.5715H1.61905C1.17196 14.5715 0.809524 14.2091 0.809524 13.762L0.817619 9.30957ZM13.7619 9.30957V13.762C13.7619 14.2091 13.3995 14.5715 12.9524 14.5715H8.5V9.30957H13.7619Z"
                      fill="#5E5E63"
                    />
                  </svg>
                ) : null}
                {method.method === RefundPaymentMethod.BankTransfer ? (
                  <>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill="currentColor"
                        d="M11.5 1L2 6v2h19V6m-5 4v7h3v-7M2 22h19v-3H2m8-9v7h3v-7m-9 0v7h3v-7z"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill="currentColor"
                        d="M23 9V3H1v6zm0 2v10H1V11zM9 17v-2H4v2z"
                      />
                    </svg>
                  </>
                )}
                <span
                  className="text-xl font-light"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      `refund_card_method${method.method}_title`
                    )
                  }}
                />
              </p>
              {isRDVSurplus ? (
                <RDVSurplusBadge
                  totalRDVSurplus={totalRDVSurplus()}
                  rdvAmount={getRDVAmount(rdvSurplus?.surplus_amount ?? 0)}
                  surplusPercentage={rdvSurplus?.surplus_percentage ?? 0}
                />
              ) : (
                <>
                  {!isReverLite && (
                    <p className="ml-2 text-xl font-light">
                      {returnTotalAmount}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <p
            className="mt-2 font-normal text-neutral-500"
            dangerouslySetInnerHTML={{
              __html: translate(
                `refund_card_method${method.method}_description${method.timing}`
              )
            }}
          />
          {keepYourItemOffered ? null : (
            <ShippingCosts
              shippingCosts={shippingCosts}
              isInstantMethod={instantMethod}
              hideMinimumReturnCost={giftCardOffered}
            />
          )}
          <p className="text-xs text-neutral-400">
            {t('service_fees.tooltip_message', {
              fee: serviceFeeAmount()
            })}
          </p>

          <div>
            {keepYourItemOffered && (
              <p className="flex items-center gap-2 text-xs text-grey-2">
                {translate('kyi_refund_of')} {totalRDVSurplus()}{' '}
                {instantMethod ? null : (
                  <span className="flex items-center text-orange-400">
                    - {maxShippingCots}
                    <DeliveryIcon />
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex justify-between md:gap-6">
            <div />
            {method.timing === RefundTimings.OnStartReturnProcess ? (
              <div
                className={`mt-2 self-center whitespace-nowrap rounded-l-lg py-0 text-center ${selected ? 'animate-slide-in-from-left' : 'animate-slide-out-from-left'}`}
                style={{
                  width: '80px',
                  backgroundColor: tagBackgroundColor,
                  color: tagColor,
                  marginRight: selected ? '-10px' : '-12px',
                  transformOrigin: 'right'
                }}
              >
                <p
                  dangerouslySetInnerHTML={{
                    __html: translate(`refund_card_time_method${method.method}`)
                  }}
                  className="text-[0.6rem] leading-[8px]"
                />
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (isMisako) {
    return (
      <div
        id={method.method?.toString()}
        className="relative flex content-center items-center"
        onClick={() => {
          dispatch(setRefundPaymentMethod(method ?? null))
          dispatch(setKeepYourItem(false))
        }}
      >
        <div
          className={clsx(
            'w-full max-w-[35rem] cursor-pointer rounded-sm border border-solid border-neutral-300 p-4 transition-all hover:border-neutral-400 hover:bg-neutral-100/60',
            { 'border-neutral-400 bg-neutral-100/60': selected }
          )}
        >
          <div className="flex justify-between gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <p
                  className="mt-0.5 text-[15px] font-[300] tracking-wider"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      `refund_card_method${method.method}_title`
                    )
                  }}
                />
                {instantMethod && freeAvailable ? (
                  <img src={logoUrl} className="w-24" />
                ) : null}
              </div>
            </div>
            {instantMethod && freeAvailable ? (
              <p
                className="ml-2 mr-auto bg-neutral-900 px-2 py-0.5 text-xs leading-normal tracking-wider text-white"
                dangerouslySetInnerHTML={{
                  __html: translate('refund_card_free_shipping')
                }}
              />
            ) : null}
            {!serviceFees?.apply_only_if_payment_at_checkout ? (
              <div className="flex items-center justify-center">
                <Tooltip
                  content={t('service_fees.tooltip_message', {
                    fee: serviceFeeAmount()
                  })}
                  classNames={{
                    content:
                      'max-w-32 rounded bg-neutral-800 px-2 py-1 text-xs text-white'
                  }}
                >
                  <p className="flex size-4 items-center justify-center self-start rounded-full bg-grey-6 text-grey-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1em"
                      height="1em"
                      viewBox="0 0 512 512"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="40"
                        d="M196 220h64v172"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeMiterlimit="10"
                        strokeWidth="40"
                        d="M187 396h138"
                      />
                      <path
                        fill="currentColor"
                        d="M256 160a32 32 0 1 1 32-32a32 32 0 0 1-32 32"
                      />
                    </svg>
                  </p>
                </Tooltip>
              </div>
            ) : null}
          </div>
          {keepYourItemOffered ? null : (
            <ShippingCosts
              shippingCosts={shippingCosts}
              isInstantMethod={instantMethod}
              hideMinimumReturnCost={giftCardOffered}
            />
          )}
          {keepYourItemOffered && (
            <p className="flex items-center gap-2 text-xs text-grey-2">
              {translate('kyi_refund_of')} {totalRDVSurplus()}{' '}
              {instantMethod ? null : (
                <span className="flex items-center text-orange-400">
                  - {maxShippingCots}
                </span>
              )}
            </p>
          )}
          <div className="mt-2 flex items-end justify-between gap-4">
            <p
              className="max-w-96 text-[13px] font-[300] leading-5 tracking-wider"
              style={{
                color: selected
                  ? theme.colors.common.black
                  : theme.colors.grey[0]
              }}
              dangerouslySetInnerHTML={{
                __html: translate(
                  `refund_card_method${method.method}_description${method.timing}`
                )
              }}
            />
            <div className="flex flex-col items-end pb-1">
              {isRDVSurplus ? (
                <RDVSurplusBadge
                  totalRDVSurplus={totalRDVSurplus()}
                  rdvAmount={getRDVAmount(rdvSurplus?.surplus_amount ?? 0)}
                  surplusPercentage={rdvSurplus?.surplus_percentage ?? 0}
                />
              ) : (
                <>
                  {!isReverLite && (
                    <div className="w-fit font-light">{returnTotalAmount}</div>
                  )}
                </>
              )}

              <div className="flex justify-between md:gap-6">
                {method.timing === RefundTimings.OnStartReturnProcess ? (
                  <div className="mt-1 font-[300] text-neutral-500">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: translate(
                          `refund_card_time_method${method.method}`
                        )
                      }}
                      className="text-[0.6rem] leading-[8px]"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      id={method.method?.toString()}
      className="relative flex content-center items-center"
      onClick={() => {
        dispatch(setRefundPaymentMethod(method ?? null))
        dispatch(setKeepYourItem(false))
      }}
    >
      {instantMethod && freeAvailable ? (
        <div className="absolute left-4 top-[-10px] rounded-[20px] border border-[#1b75eb] bg-[radial-gradient(circle_at_78px_10px,_#0076e7,_#07a5fe_100%)] px-[0.8rem] py-[0.1rem] text-white">
          <p className="text-xs">
            <b
              dangerouslySetInnerHTML={{
                __html: translate('refund_card_free_shipping')
              }}
            />
          </p>
        </div>
      ) : null}
      <div
        className="mb-6 w-full cursor-pointer rounded"
        style={{
          padding: selected ? '12px 10px' : '14px 12px',
          borderStyle: 'solid',
          borderWidth: selected ? '2px' : '0px',
          borderColor: selected
            ? instantMethod
              ? '#1b75eb'
              : theme.colors.primary.main
            : 'transparent',

          backgroundColor: selected
            ? instantMethod
              ? 'rgba(27,117,235,0.1)'
              : toRGB(theme.colors.primary.main)
            : 'transparent',

          boxShadow: selected ? 'none' : '0px 0px 2px 2px rgb(0 0 0 / 0.1)'
        }}
      >
        <div className="flex justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="flex items-center gap-2">
              {instantMethod ? (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={
                    selected
                      ? 'z-[5] size-[15px] overflow-visible [&>#TOP]:animate-spin-gift'
                      : 'z-[5] size-[15px] overflow-visible'
                  }
                >
                  <path
                    id="TOP"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.5714 6.881V4.85719C14.5714 4.41011 14.209 4.04767 13.7619 4.04767H9.27714L11.819 2.10481C11.9908 1.97599 12.1043 1.78421 12.1347 1.57167C12.1651 1.35913 12.1097 1.14323 11.981 0.971475L11.4952 0.323856C11.227 -0.0338126 10.7196 -0.1063 10.3619 0.161951L7.28571 2.53386L4.20952 0.161951C3.85185 -0.1063 3.34444 -0.0338126 3.07619 0.323856L2.59048 0.971475C2.32222 1.32915 2.39471 1.83656 2.75238 2.10481L5.29429 4.04767H0.809524C0.36244 4.04767 0 4.41011 0 4.85719V6.881H14.5714Z"
                    fill="#5E5E63"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.817619 9.30957H6.07952L6.07143 14.5715H1.61905C1.17196 14.5715 0.809524 14.2091 0.809524 13.762L0.817619 9.30957ZM13.7619 9.30957V13.762C13.7619 14.2091 13.3995 14.5715 12.9524 14.5715H8.5V9.30957H13.7619Z"
                    fill="#5E5E63"
                  />
                </svg>
              ) : (
                <img
                  src={iconSource}
                  alt=""
                  data-selected={selected}
                  className="size-[15px] data-[selected=true]:animate-spin-icon"
                />
              )}
              <span
                className="text-lg font-bold"
                dangerouslySetInnerHTML={{
                  __html: translate(`refund_card_method${method.method}_title`)
                }}
              />
            </p>
            {isRDVSurplus ? (
              <RDVSurplusBadge
                totalRDVSurplus={totalRDVSurplus()}
                rdvAmount={getRDVAmount(rdvSurplus?.surplus_amount ?? 0)}
                surplusPercentage={rdvSurplus?.surplus_percentage ?? 0}
              />
            ) : (
              <>
                {!isReverLite && (
                  <div
                    className={
                      'w-fit rounded-lg border-solid border-grey-4 px-2 text-base font-light ' +
                      (freeShipping ||
                      (freeAvailable && instantMethod) ||
                      !giftCardOffered
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-grey-5 text-grey-1')
                    }
                  >
                    <p>{returnTotalAmount}</p>
                  </div>
                )}
              </>
            )}
          </div>
          {shouldShowServiceFees ? (
            <Tooltip
              content={t('service_fees.tooltip_message', {
                fee: serviceFeeAmount()
              })}
              classNames={{
                content:
                  'max-w-32 rounded bg-neutral-800 px-2 py-1 text-xs text-white'
              }}
            >
              <p className="flex size-4 items-center justify-center self-start rounded-full bg-grey-6 text-grey-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="40"
                    d="M196 220h64v172"
                  />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeMiterlimit="10"
                    strokeWidth="40"
                    d="M187 396h138"
                  />
                  <path
                    fill="currentColor"
                    d="M256 160a32 32 0 1 1 32-32a32 32 0 0 1-32 32"
                  />
                </svg>
              </p>
            </Tooltip>
          ) : null}
        </div>

        {keepYourItemOffered ? null : (
          <ShippingCosts
            shippingCosts={shippingCosts}
            isInstantMethod={instantMethod}
            hideMinimumReturnCost={giftCardOffered}
          />
        )}

        <div>
          {keepYourItemOffered && (
            <p className="flex items-center gap-2 text-xs text-grey-2">
              {translate('kyi_refund_of')} {totalRDVSurplus()}{' '}
              {instantMethod ? null : (
                <span className="flex items-center text-orange-400">
                  - {maxShippingCots}
                  <DeliveryIcon />
                </span>
              )}
            </p>
          )}
        </div>
        <p
          className="mt-2 font-normal"
          style={{
            color: selected ? theme.colors.common.black : theme.colors.grey[0]
          }}
          dangerouslySetInnerHTML={{
            __html: translate(
              `refund_card_method${method.method}_description${method.timing}`
            )
          }}
        />

        <div className="flex justify-between md:gap-6">
          <div />
          {method.timing === RefundTimings.OnStartReturnProcess ? (
            <div
              className={`mt-2 self-center whitespace-nowrap rounded-l-lg py-0 text-center ${selected ? 'animate-slide-in-from-left' : 'animate-slide-out-from-left'}`}
              style={{
                width: '80px',
                backgroundColor: tagBackgroundColor,
                color: tagColor,
                marginRight: selected ? '-10px' : '-12px',
                transformOrigin: 'right'
              }}
            >
              <p
                dangerouslySetInnerHTML={{
                  __html: translate(`refund_card_time_method${method.method}`)
                }}
                className="text-[0.6rem] leading-[8px]"
              />
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  )
}

export default RefundCard
